// Lines — the parts of a course or module page as objects among the text
// (pen "Always writing", piece 3, "Tile and face"): a kind tile, the name in
// the interface face, one quiet meta (its kind, then what it holds or its
// state: "Page", "Page · Draft", "Module · 3 parts"), "N suggested" in the
// assistant's colour, and a module's parts on a second line. Greys only, so
// an object never reads as the author's prose. Pointing at one shows its
// edge, a sunken fill and an open arrow; the whole tile opens it. Tiles
// stack tight. A long name or part list ends in … with the whole on hover.
//
// `PartLine` is one part, as it stands among a page's text (the editor
// draws it where its include node is); `Lines` lists parts on a page with
// no text of its own (the project's own page with no single course) or
// while the page's text loads.
//
// Every line is a row of the editor grid: the margin column, then the line.
// Pointing at a line shows its controls in the margin: + (add a line under
// it, the Create menu from the host) and the grip (drag to move it among its
// container's parts; a click opens the handle menu: Open, Rename, Duplicate,
// Move to ›, Move to Trash). A module also has a chevron there that folds
// and unfolds its parts in place, one level.
//
// A line that asks (Ask here from a button, pen Q66; `ask`, rendered by the
// host) stands among them where the part it asks for would land.
//
// Pure: the links are projected in; every choice goes out as an intent.
// Local state is view-ephemeral only: which modules are unfolded, which line
// is being renamed or dragged, the open menu.

export type { LineAsk, LineIntents };
export { AskRow, Lines, PartLine, useKindWord, useStateWord };

import * as Limits from "@bilda/core/limits";
import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type PointerEvent, type ReactNode, useRef, useState } from "react";
import { SuggestedMark } from "../AiMarker/AiMarker.js";
import { ChangeMarker } from "../Editor/ChangeMarker/ChangeMarker.js";
import { EditableText } from "../Editor/EditableText/EditableText.js";
import { Icon } from "../Icon/Icon.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import { reorderTarget } from "../ModuleCard/partOps.js";
import type { DeskCommand } from "../Studio/studioDesk.js";
import type { DocState } from "../StudioDesk/deskFacts.js";
import { useLandingMarks } from "../StudioLanding/landingMarks.js";
import { kindIcon } from "./Outline.js";
import type { LandingChoice } from "./studioCreate.js";
import type { LinkNote, PageLink, PageNodeKind } from "./studioPages.js";

type LineIntents = {
    readonly onOpen: (commands: readonly DeskCommand[]) => void;
    // A listed part to another place in its container.
    readonly onMove: (link: PageLink, to: number) => void;
    // A listed part into another container (at its end).
    readonly onMoveTo: (link: PageLink, container: string) => void;
    // Out of its container, with an Undo (there is no Trash yet).
    readonly onTrash: (link: PageLink) => void;
    readonly onRename: (link: PageLink, title: string) => void;
    // A module in a course: its next one (absent: not offered here).
    readonly onDuplicate: ((link: PageLink) => void) | undefined;
    // The + in a line's margin: the Create menu, landing under it.
    readonly renderAdd: (link: PageLink) => ReactNode;
};

// A line that asks, before the part at `at` in the page's container (absent:
// after the last).
type LineAsk = {
    readonly at: number | undefined;
    readonly line: ReactNode;
};

// How far a pointer moves on the grip before it drags (else it is a click).
const DRAG_FROM_PX = 4;

// The kind's own word (a hook: the words are the reader's locale).
const useKindWord = (): ((kind: PageNodeKind) => string) => {
    const { t } = useLingui();
    return (kind) => {
        switch (kind) {
            case "course":
                return t`Course`;
            case "module":
                return t`Module`;
            case "page":
                return t`Page`;
            case "quiz":
                return t`Quiz`;
            case "source":
                return t`Source`;
            case "file":
                return t`File`;
            default:
                return exhaustSwitchCase(kind);
        }
    };
};

// A document's state in a word (null: it says nothing beyond its kind).
const useStateWord = (): ((state: DocState, ai: boolean) => string | null) => {
    const { t } = useLingui();
    return (state, ai) => {
        switch (state) {
            case "generated":
                return ai ? t`Draft` : null;
            case "planned":
                return t`Planned`;
            case "writing":
                return t`Writing …`;
            case "empty":
                return t`Empty`;
            case "removed":
                return t`Removed`;
            case "source":
            case "unknown":
                return null;
            default:
                return exhaustSwitchCase(state);
        }
    };
};

// A line's meta: its kind, then what it holds or its state ("Module · 3
// parts", "Page · Draft"; a kind alone when there is nothing to add). A
// module or course with no parts yet is Planned (laid out, nothing in it
// yet: owner, 30 Oct, the New project box's modules).
const useMetaWord = (): ((kind: PageNodeKind, note: LinkNote) => string) => {
    const { t } = useLingui();
    const kindWord = useKindWord();
    const stateWord = useStateWord();
    return (kind, note) => {
        const more = ((): string | null => {
            switch (note.type) {
                case "parts":
                    return note.count === 0
                        ? t`Planned`
                        : t`${plural(note.count, { one: "# part", other: "# parts" })}`;
                case "doc":
                    return stateWord(note.state, note.ai);
                default:
                    return exhaustSwitchCase(note);
            }
        })();
        return more === null ? kindWord(kind) : `${kindWord(kind)} · ${more}`;
    };
};

type Drag = {
    readonly id: string;
    readonly startY: number;
    readonly y: number;
    readonly moved: boolean;
};

// One part among a page's text (or in a list of them). `nested`: a part of
// an unfolded module, one step in, with no controls of its own.
// `standalone`: not inside a list (a tile the editor draws among the text).
const PartLine = ({
    link,
    online,
    places,
    intents,
    nested = false,
    standalone = false,
}: {
    readonly link: PageLink;
    readonly online: boolean;
    // Every container a part can move to (Move to ›).
    readonly places: readonly LandingChoice[];
    readonly intents: LineIntents;
    readonly nested?: boolean;
    readonly standalone?: boolean;
}): ReactNode => {
    const { t } = useLingui();
    const metaWord = useMetaWord();
    const [open, setOpen] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [drag, setDrag] = useState<Drag | null>(null);
    // How it lands (while its panel is open): this part's mark, and the tint.
    const landing = useLandingMarks();
    const mark = nested ? undefined : landing?.parts.get(link.id);
    // Rename was picked: once the menu has closed (it holds the focus until
    // then), the name becomes a field with the caret in it, which keeps it.
    const toRename = useRef(false);
    const keepCaret = useRef(false);

    // Where a drop at `y` puts it among its container's parts (their tiles
    // on the page, wherever they stand among the text).
    const dropAt = (y: number): number | undefined => {
        const holder = link.holder;
        if (holder === undefined) return undefined;
        const peers = holder.container.parts.flatMap((p, at) => {
            const el = document.querySelector(
                `[data-line="${CSS.escape(p.id)}"]:not([data-nested])`,
            );
            const box = el?.getBoundingClientRect();
            return box === undefined
                ? []
                : [{ at, middle: box.top + box.height / 2 }];
        });
        const from = peers.findIndex((p) => p.at === holder.at);
        const to = reorderTarget(
            peers.map((p) => p.middle),
            from,
            y,
        );
        return peers[to]?.at;
    };

    const gripDown = (e: PointerEvent) => {
        if (e.button !== 0 || !online || link.holder === undefined) return;
        // The grip opens its menu on a click, not on press: a press may drag.
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag({ id: link.id, startY: e.clientY, y: e.clientY, moved: false });
    };
    const gripMove = (e: PointerEvent) =>
        setDrag((d) =>
            d === null
                ? d
                : {
                      ...d,
                      y: e.clientY,
                      moved:
                          d.moved ||
                          Math.abs(e.clientY - d.startY) > DRAG_FROM_PX,
                  },
        );
    const gripUp = (e: PointerEvent) => {
        const d = drag;
        setDrag(null);
        if (d === null) return;
        if (!d.moved) return setMenuOpen(true);
        const to = dropAt(e.clientY);
        if (to !== undefined && to !== link.holder?.at)
            intents.onMove(link, to);
    };

    const menuItems = (): readonly MenuItem[] => {
        const holder = link.holder;
        if (holder === undefined) return [];
        const last = holder.container.parts.length - 1;
        const duplicate = intents.onDuplicate;
        const elsewhere = places.filter(
            (p) => p.id !== holder.container.id && p.id !== link.id,
        );
        return [
            {
                _tag: "item",
                label: t`Open`,
                icon: <Icon name="arrow-up-right" />,
                onSelect: () => intents.onOpen(link.open),
            },
            {
                _tag: "item",
                label: t`Rename`,
                icon: <Icon name="pencil" />,
                disabled: !online,
                onSelect: () => {
                    toRename.current = true;
                },
            },
            ...(duplicate === undefined ||
            link.kind !== "module" ||
            holder.container.kind !== "course"
                ? []
                : [
                      {
                          _tag: "item",
                          label: t`Duplicate`,
                          icon: <Icon name="copy" />,
                          disabled: !online,
                          onSelect: () => duplicate(link),
                      } satisfies MenuItem,
                  ]),
            {
                _tag: "submenu",
                label: t`Move to`,
                icon: <Icon name="answer-line" />,
                disabled: !online,
                items: [
                    {
                        _tag: "item",
                        label: t`Up`,
                        icon: <Icon name="arrow-up" />,
                        disabled: holder.at === 0,
                        onSelect: () => intents.onMove(link, holder.at - 1),
                    },
                    {
                        _tag: "item",
                        label: t`Down`,
                        icon: <Icon name="arrow-down" />,
                        disabled: holder.at === last,
                        onSelect: () => intents.onMove(link, holder.at + 1),
                    },
                    ...(elsewhere.length === 0
                        ? []
                        : [
                              { _tag: "separator" } satisfies MenuItem,
                              ...elsewhere.map(
                                  (p): MenuItem => ({
                                      _tag: "item",
                                      label: p.title,
                                      icon: <Icon name="folder" />,
                                      onSelect: () =>
                                          intents.onMoveTo(link, p.id),
                                  }),
                              ),
                          ]),
                ],
            },
            { _tag: "separator" },
            {
                _tag: "item",
                label: t`Move to Trash`,
                icon: <Icon name="trash" />,
                disabled: !online,
                onSelect: () => intents.onTrash(link),
            },
        ];
    };

    const isModule = link.kind === "module";
    const controls = !nested && link.holder !== undefined;
    const name = renaming ? (
        // A focus leaving the field ends the rename (kept or not).
        // biome-ignore lint/a11y/noStaticElementInteractions: focusout from the field inside, not a control of its own.
        <span className="bilda-line__name" onBlur={() => setRenaming(false)}>
            <EditableText
                value={link.title}
                ariaLabel={t`Name`}
                maxLength={Limits.SHORT_TEXT_MAX}
                autoEdit
                onCommit={(text) => {
                    const next = text.replace(/\s+/g, " ").trim();
                    if (next !== "" && next !== link.title)
                        intents.onRename(link, next);
                    setRenaming(false);
                }}
            />
        </span>
    ) : (
        <span className="bilda-line__name" title={link.title}>
            {link.title}
        </span>
    );
    // A module's parts, in order, on a second line.
    const partNames = link.links.map((l) => l.title).join(" · ");
    const face = (
        <>
            <span className="bilda-line__tile">
                <Icon name={kindIcon(link.kind)} size="sm" />
            </span>
            <span className="bilda-line__text">
                <span className="bilda-line__head">
                    {name}
                    {link.suggestion ? <SuggestedMark /> : null}
                    <span className="bilda-line__note">
                        {metaWord(link.kind, link.note)}
                    </span>
                    {link.suggested > 0 ? (
                        <span className="bilda-line__suggested">
                            {t`${link.suggested} suggested`}
                        </span>
                    ) : null}
                </span>
                {isModule && partNames !== "" ? (
                    <span className="bilda-line__parts" title={partNames}>
                        {partNames}
                    </span>
                ) : null}
            </span>
            <span className="bilda-line__open">
                <Icon name="arrow-up-right" size="sm" />
            </span>
        </>
    );
    const Root = standalone ? "div" : "li";
    return (
        <Root
            className="bilda-line"
            data-line={link.id}
            data-kind={link.kind}
            data-nested={nested ? "" : undefined}
            data-dragging={drag?.moved === true ? "" : undefined}
            data-menu={menuOpen ? "" : undefined}
            data-landing-pointed={
                !nested && landing?.pointed.has(link.id) ? "" : undefined
            }
        >
            <div className="bilda-line__row">
                <div className="bilda-line__margin">
                    {mark === undefined || landing === null ? null : (
                        <ChangeMarker
                            marker={{ type: "landing", mark }}
                            onActivate={() => landing.onPoint([link.id])}
                        />
                    )}
                    {controls ? intents.renderAdd(link) : null}
                    {controls ? (
                        <Menu
                            align="start"
                            open={menuOpen}
                            onOpenChange={(o) => {
                                setMenuOpen(o);
                                if (o || !toRename.current) return;
                                toRename.current = false;
                                keepCaret.current = true;
                                setRenaming(true);
                            }}
                            contentClassName="bilda-line__menu"
                            items={menuItems()}
                            returnFocus={() => {
                                const back = !keepCaret.current;
                                keepCaret.current = false;
                                return back;
                            }}
                            trigger={
                                <button
                                    type="button"
                                    className="bilda-line__control bilda-line__grip"
                                    aria-label={t`Move or change ${link.title}`}
                                    onPointerDown={gripDown}
                                    onPointerMove={gripMove}
                                    onPointerUp={gripUp}
                                >
                                    <Icon name="grip" size="sm" />
                                </button>
                            }
                        />
                    ) : null}
                    {isModule && link.links.length > 0 ? (
                        <button
                            type="button"
                            className="bilda-line__control bilda-line__fold"
                            aria-expanded={open}
                            aria-label={
                                open
                                    ? t`Fold ${link.title}`
                                    : t`Unfold ${link.title}`
                            }
                            onClick={() => setOpen((o) => !o)}
                        >
                            <Icon
                                name={open ? "chevron-down" : "chevron-right"}
                                size="sm"
                            />
                        </button>
                    ) : null}
                </div>
                {renaming ? (
                    <div className="bilda-line__line">{face}</div>
                ) : (
                    <button
                        type="button"
                        className="bilda-line__line"
                        onClick={() => intents.onOpen(link.open)}
                    >
                        {face}
                    </button>
                )}
            </div>
            {open ? (
                <ul className="bilda-lines">
                    {link.links.map((l) => (
                        <PartLine
                            key={l.id}
                            link={l}
                            online={online}
                            places={places}
                            intents={intents}
                            nested
                        />
                    ))}
                </ul>
            ) : null}
        </Root>
    );
};

const Lines = ({
    links,
    online,
    places,
    intents,
    ask,
}: {
    readonly links: readonly PageLink[];
    readonly online: boolean;
    // Every container a part can move to (Move to ›).
    readonly places: readonly LandingChoice[];
    readonly intents: LineIntents;
    readonly ask?: LineAsk;
}): ReactNode => {
    const line = (link: PageLink) => (
        <PartLine
            key={link.id}
            link={link}
            online={online}
            places={places}
            intents={intents}
        />
    );
    // The asking line goes before the first part at or past its place.
    const place = ask?.at;
    const past =
        place === undefined
            ? -1
            : links.findIndex((l) => (l.holder?.at ?? -1) >= place);
    const askAt = ask === undefined ? -1 : past < 0 ? links.length : past;
    const askRow =
        ask === undefined ? null : <AskRow key="ask" line={ask.line} />;
    return (
        <ul className="bilda-lines">
            {links.flatMap((l, i) =>
                i === askAt ? [askRow, line(l)] : [line(l)],
            )}
            {askAt === links.length ? askRow : null}
        </ul>
    );
};

// A line that asks, in a page's grid (its sparkle in the margin).
// `standalone`: not inside a list (among a page's text).
const AskRow = ({
    line,
    standalone = false,
}: {
    readonly line: ReactNode;
    readonly standalone?: boolean;
}): ReactNode => {
    const Root = standalone ? "div" : "li";
    return (
        <Root className="bilda-line bilda-line--ask">
            <div className="bilda-line__row">
                <div className="bilda-line__margin" />
                {line}
            </div>
        </Root>
    );
};
